import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'
import Portrait from '../components/Portrait'
import { api, ApiError } from '../lib/api'
import { formatTimestamp } from '../lib/datetime'
import type { ResultsPick, ResultsPlayer, ResultsResponse, ResultsTeam } from '../types/protocol'

// The board's position tokens, tinted rather than solid: this page is read at
// phone distance, where a wall of saturated fills is noise. Written out whole
// for the same reason POSITION_FILL is — Tailwind never sees an interpolation.
const POSITION_STYLES: Record<string, string> = {
  QB: 'bg-pos-qb/15 text-pos-qb',
  RB: 'bg-pos-rb/15 text-pos-rb',
  WR: 'bg-pos-wr/15 text-pos-wr',
  TE: 'bg-pos-te/15 text-pos-te',
  K: 'bg-pos-k/15 text-pos-k',
  DST: 'bg-pos-dst/20 text-pos-dst',
}

// The left rail on a roster row — the same hue as the chip, so a roster reads
// as a column of positions before it reads as a column of names.
const POSITION_RAIL: Record<string, string> = {
  QB: 'bg-pos-qb',
  RB: 'bg-pos-rb',
  WR: 'bg-pos-wr',
  TE: 'bg-pos-te',
  K: 'bg-pos-k',
  DST: 'bg-pos-dst',
}

const MADE_BY_LABELS: Record<string, string> = {
  owner: 'Owner',
  commissioner: 'Commissioner',
  auto: 'Autopick',
}

// The players table stores Sleeper's "DEF"; rosters and the draft UI call the
// same thing DST.
function displayPosition(position: string): string {
  return position === 'DEF' ? 'DST' : position
}

export default function Results() {
  const { code = '' } = useParams()
  const joinCode = code.toUpperCase()
  const [data, setData] = useState<ResultsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // A one-shot read, no cable subscription. Results are a snapshot people
  // open after the fact or share mid-draft; a reload is the refresh button.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api<ResultsResponse>(`/drafts/${joinCode}/results`)
      .then((response) => {
        if (cancelled) return
        setData(response)
        setError(null)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        if (e instanceof ApiError && e.status === 404) setError(`No draft found for code ${joinCode}.`)
        else setError(e instanceof ApiError ? e.message : 'Could not load the results')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [joinCode])

  if (loading) {
    return (
      <main className="min-h-screen px-4 py-16 text-center text-sm text-ink-lo">
        Loading results…
      </main>
    )
  }

  if (error || !data) {
    return (
      <main className="min-h-screen px-4 py-16 text-center">
        <p className="text-crit">{error ?? 'No results to show'}</p>
        <Link
          to="/"
          className="mt-6 inline-block text-sm text-ink-lo underline-offset-4 hover:text-ink-hi hover:underline"
        >
          Back to start
        </Link>
      </main>
    )
  }

  const { draft, picks, teams } = data
  const madeCount = picks.filter((pick) => pick.status === 'made').length

  return (
    <main className="min-h-screen px-4 py-8">
      <div className="mx-auto flex max-w-6xl flex-col gap-8">
        <header className="flex flex-wrap items-end justify-between gap-4 border-b border-line pb-6">
          <div>
            <p className="eyebrow">
              {draft.completed_at ? 'Final results' : 'Results so far'}
            </p>
            <h1 className="mt-2 font-display text-5xl leading-none font-bold text-ink-hi">
              {draft.name}
            </h1>
            <p className="mt-3 text-sm text-ink-lo">
              Code{' '}
              <span className="font-mono tracking-[0.2em] text-signal">{draft.join_code}</span>
              {' · '}
              {draft.draft_type} · {draft.rounds} rounds ·{' '}
              <span className="tabular-nums">
                {madeCount} of {picks.length}
              </span>{' '}
              picks made
            </p>
            <p className="mt-1 text-sm text-ink-lo">
              {draft.completed_at
                ? `Completed ${formatTimestamp(draft.completed_at)}`
                : draft.started_at
                  ? `Started ${formatTimestamp(draft.started_at)}`
                  : 'Not started yet'}
            </p>
          </div>

          <a
            href={`/drafts/${draft.join_code}/export.csv`}
            className="rounded-md border border-line-strong bg-surface-3 px-5 py-2.5 text-sm font-semibold text-ink-mid shadow-raised hover:text-ink-hi"
          >
            Download CSV
          </a>
        </header>

        {picks.length === 0 ? (
          <p className="panel p-6 text-sm text-ink-lo">
            This draft hasn&apos;t started, so there are no picks yet. The CSV download and the
            rosters below will fill in once it does.
          </p>
        ) : null}

        <RosterCards teams={teams} />
        {picks.length > 0 && <PickList picks={picks} />}
      </div>
    </main>
  )
}

function RosterCards({ teams }: { teams: ResultsTeam[] }) {
  if (teams.length === 0) {
    return <p className="panel p-6 text-sm text-ink-lo">No teams in this draft.</p>
  }

  return (
    <section>
      <h2 className="eyebrow">Rosters</h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {teams.map((team) => (
          <div key={team.id} className="panel overflow-hidden">
            <div className="flex items-baseline justify-between gap-2 border-b border-line bg-surface-2 px-5 py-4">
              <div className="min-w-0">
                <h3 className="truncate font-display text-xl leading-tight font-bold text-ink-hi">
                  {team.name}
                </h3>
                <p className="mt-0.5 truncate text-sm text-ink-lo">
                  {team.owner_name ?? 'Unclaimed'}
                </p>
              </div>
              <span className="shrink-0 font-mono text-xs text-signal tabular-nums">
                #{team.draft_position ?? '—'}
              </span>
            </div>

            {team.roster.length === 0 ? (
              <p className="px-5 py-4 text-sm text-ink-lo">No players drafted.</p>
            ) : (
              <ul className="flex flex-col gap-1 p-3">
                {team.roster.map((entry) => (
                  <li
                    key={entry.overall}
                    className="flex items-center gap-2.5 overflow-hidden rounded-md bg-ink/40 py-1.5 pr-2.5 pl-0"
                  >
                    {/* The rail carries the position colour so a roster scans as
                        a shape before it is read as a list of names. */}
                    <span
                      aria-hidden="true"
                      className={`h-9 w-1 shrink-0 rounded-r-sm ${
                        POSITION_RAIL[displayPosition(entry.player.position)] ?? 'bg-ink-lo'
                      }`}
                    />
                    <span className="w-10 shrink-0 font-mono text-[10px] font-semibold tracking-wide text-ink-lo uppercase">
                      {entry.slot}
                    </span>
                    <Portrait
                      fullName={entry.player.name}
                      position={entry.player.position}
                      nflTeam={entry.player.nfl_team}
                      src={entry.player.portrait_url}
                      size="lg"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm leading-tight text-ink-hi">
                        {entry.player.name}
                      </span>
                      <span className="mt-1 flex items-center gap-1.5">
                        <PositionChip position={entry.player.position} />
                        <span className="font-mono text-[10px] text-ink-lo tabular-nums">
                          {entry.player.nfl_team ?? 'FA'} · #{entry.overall}
                        </span>
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}

function PickList({ picks }: { picks: ResultsPick[] }) {
  return (
    <section>
      <h2 className="eyebrow">Every pick</h2>
      <div className="panel mt-4 overflow-x-auto p-4">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-line">
              <th className="py-2 pr-3 font-mono text-[10px] font-semibold tracking-[0.14em] text-ink-lo uppercase">
                #
              </th>
              <th className="py-2 pr-3 font-mono text-[10px] font-semibold tracking-[0.14em] text-ink-lo uppercase">
                Rd.Pick
              </th>
              <th className="py-2 pr-3 font-mono text-[10px] font-semibold tracking-[0.14em] text-ink-lo uppercase">
                Team
              </th>
              <th className="py-2 pr-3 font-mono text-[10px] font-semibold tracking-[0.14em] text-ink-lo uppercase">
                Player
              </th>
              <th className="py-2 pr-3 font-mono text-[10px] font-semibold tracking-[0.14em] text-ink-lo uppercase">
                Pos
              </th>
              <th className="py-2 pr-3 font-mono text-[10px] font-semibold tracking-[0.14em] text-ink-lo uppercase">
                NFL
              </th>
              <th className="py-2 font-mono text-[10px] font-semibold tracking-[0.14em] text-ink-lo uppercase">
                By
              </th>
            </tr>
          </thead>
          <tbody>
            {picks.map((pick) => (
              <tr key={pick.overall} className="border-t border-line/60 hover:bg-surface-2">
                <td className="py-1.5 pr-3 font-mono text-xs text-ink-lo tabular-nums">
                  {pick.overall}
                </td>
                <td className="py-1.5 pr-3 font-mono text-xs whitespace-nowrap text-ink-lo tabular-nums">
                  {pick.round}.{pick.slot}
                </td>
                <td className="py-1.5 pr-3 whitespace-nowrap text-ink-mid">{pick.team_name}</td>
                <td className="py-1.5 pr-3">
                  <span className="flex items-center gap-2">
                    {pick.player && (
                      <Portrait
                        fullName={pick.player.name}
                        position={pick.player.position}
                        nflTeam={pick.player.nfl_team}
                        src={pick.player.portrait_url}
                        size="xs"
                      />
                    )}
                    <PlayerCell player={pick.player} status={pick.status} />
                  </span>
                </td>
                <td className="py-1.5 pr-3">
                  {pick.player ? <PositionChip position={pick.player.position} /> : null}
                </td>
                <td className="py-1.5 pr-3 font-mono text-xs text-ink-lo">
                  {pick.player?.nfl_team ?? '—'}
                </td>
                <td className="py-1.5">
                  {pick.made_by ? (
                    <span className="rounded-full border border-line px-2 py-0.5 font-mono text-[10px] text-ink-lo">
                      {MADE_BY_LABELS[pick.made_by] ?? pick.made_by}
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function PlayerCell({ player, status }: { player: ResultsPlayer | null; status: ResultsPick['status'] }) {
  if (player) return <span className="whitespace-nowrap text-ink-hi">{player.name}</span>
  // A skip is an absence, not a warning — the old amber is gone everywhere.
  if (status === 'skipped') return <span className="text-ink-lo italic">Skipped</span>
  return <span className="text-ink-lo">On the clock later</span>
}

function PositionChip({ position }: { position: string }) {
  const label = displayPosition(position)
  const style = POSITION_STYLES[label] ?? 'bg-pos-dst/20 text-pos-dst'
  return (
    <span
      className={`shrink-0 rounded-sm px-1.5 py-0.5 font-mono text-[10px] font-semibold ${style}`}
    >
      {label}
    </span>
  )
}
